function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function sanitizeWizardAssistantMessage(content: string): string {
  if (!content) {
    return "";
  }

  return content
    .replace(/\{\{\s*loop\.item\s*\}\}/g, "current loop item")
    .replace(
      /\{\{\s*custom_field\.([A-Za-z0-9.]+)\s*\}\}/g,
      (_match, keyPath) => `custom field "${keyPath}"`
    )
    .replace(
      /\bstep_(\d+)\.output\.([A-Za-z0-9_.]+)\b/g,
      (_match, stepNumber, outputPath) =>
        `the "${outputPath}" output from step ${stepNumber}`
    )
    .replace(
      /\bstep_(\d+)\.output\b/g,
      (_match, stepNumber) => `step ${stepNumber} output`
    );
}

function applyInlineMarkdown(text: string): string {
  return (
    text
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )
      .replace(/`([^`]+)`/g, '<code class="wizard-inline-code">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/__([^_]+)__/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      // GFM disables single-underscore emphasis "intraword" (word chars on
      // both sides of the delimiter) specifically so snake_case identifiers
      // aren't mangled — e.g. a raw JSON blob with keys like `failed_step_id`
      // must never lose its underscores. Only treat _..._ as italics when
      // each underscore sits at a word boundary (surrounded by whitespace/
      // punctuation/string edges), matching that rule.
      .replace(/(?<!\w)_(?!_)([^_]+?)(?<!_)_(?!\w)/g, "<em>$1</em>")
      .replace(/~~([^~]+)~~/g, "<del>$1</del>")
  );
}

function renderParagraph(lines: string[]): string {
  return `<p>${applyInlineMarkdown(lines.join("<br>"))}</p>`;
}

export function renderWizardMarkdown(content: string): string {
  if (!content) {
    return "";
  }

  const escapedContent = escapeHtml(content).replace(/\r\n/g, "\n");
  const codeBlocks: string[] = [];
  const withCodePlaceholders = escapedContent.replace(
    /```([a-zA-Z0-9_-]+)?\n?([\s\S]*?)```/g,
    (_match, language, code) => {
      const codeBlock = [
        '<pre class="wizard-code-block">',
        language ? `<div class="wizard-code-language">${language}</div>` : "",
        `<code>${code.trim()}</code>`,
        "</pre>",
      ].join("");

      const token = `@@WIZARD_CODE_BLOCK_${codeBlocks.length}@@`;
      codeBlocks.push(codeBlock);
      return token;
    }
  );

  const lines = withCodePlaceholders.split("\n");
  const blocks: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      index += 1;
    } else if (/^@@WIZARD_CODE_BLOCK_\d+@@$/.test(trimmedLine)) {
      blocks.push(trimmedLine);
      index += 1;
    } else {
      const headingMatch = trimmedLine.match(/^(#{1,3})\s+(.*)$/);

      if (headingMatch) {
        const level = headingMatch[1].length;
        blocks.push(
          `<p class="wizard-heading wizard-heading--${level}">${applyInlineMarkdown(
            headingMatch[2]
          )}</p>`
        );
        index += 1;
      } else if (/^\s*[-*]\s+/.test(line)) {
        const items: string[] = [];
        while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
          items.push(
            `<li>${applyInlineMarkdown(
              lines[index].replace(/^\s*[-*]\s+/, "").trim()
            )}</li>`
          );
          index += 1;
        }
        blocks.push(`<ul>${items.join("")}</ul>`);
      } else if (/^\s*\d+\.\s+/.test(line)) {
        const items: string[] = [];
        while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
          items.push(
            `<li>${applyInlineMarkdown(
              lines[index].replace(/^\s*\d+\.\s+/, "").trim()
            )}</li>`
          );
          index += 1;
        }
        blocks.push(`<ol>${items.join("")}</ol>`);
      } else if (/^\s*>\s?/.test(line)) {
        const quoteLines: string[] = [];
        while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
          quoteLines.push(lines[index].replace(/^\s*>\s?/, "").trim());
          index += 1;
        }
        blocks.push(
          `<blockquote>${applyInlineMarkdown(
            quoteLines.join("<br>")
          )}</blockquote>`
        );
      } else {
        const paragraphLines: string[] = [];
        while (index < lines.length) {
          const paragraphLine = lines[index];
          const paragraphTrimmed = paragraphLine.trim();

          if (
            !paragraphTrimmed ||
            /^@@WIZARD_CODE_BLOCK_\d+@@$/.test(paragraphTrimmed) ||
            /^(#{1,3})\s+/.test(paragraphTrimmed) ||
            /^\s*[-*]\s+/.test(paragraphLine) ||
            /^\s*\d+\.\s+/.test(paragraphLine) ||
            /^\s*>\s?/.test(paragraphLine)
          ) {
            break;
          }

          paragraphLines.push(paragraphTrimmed);
          index += 1;
        }

        if (paragraphLines.length > 0) {
          blocks.push(renderParagraph(paragraphLines));
        } else {
          index += 1;
        }
      }
    }
  }

  return blocks
    .join("")
    .replace(/@@WIZARD_CODE_BLOCK_(\d+)@@/g, (_match, id) => {
      const codeBlock = codeBlocks[Number(id)];
      return codeBlock || "";
    });
}
